import type { CategoryData, CategoryFormState } from '@openx/types/targeting/category.ts';
import type { TargetingFieldsValues } from '@openx/types/targeting/targeting.ts';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import { useCallback, useEffect } from 'react';
import { useResetToAccountDefault } from '../../shared/hooks/useResetToAccountDefault.tsx';

type ResetToDefaultProps = {
  accountUid?: string;
  field: TargetingFieldsValues;
  initState: CategoryFormState;
  onFieldUpdate: (field: TargetingFieldsValues, newData: CategoryData | null) => void;
  onRemoveAll: () => void;
  updateParams: (value: CategoryFormState) => void;
};

export const useResetCategoryToAccountDefault = ({
  accountUid,
  field,
  initState,
  onFieldUpdate,
  onRemoveAll,
  updateParams,
}: ResetToDefaultProps) => {
  const { blockedCategories, inheritanceInfoComponent, shouldDisplayResetButton, shouldTriggerUpdate } =
    useResetToAccountDefault({
      accountUid,
      initState,
    });

  const handleResetToAccountDefault = useCallback(() => {
    updateParams({ op: Intersect.NOT_INTERSECTS, val: new Set(blockedCategories) });
  }, [blockedCategories, updateParams]);

  useEffect(() => {
    if (shouldTriggerUpdate) {
      if (!blockedCategories.length) {
        onRemoveAll();
        return;
      }

      updateParams({ op: Intersect.NOT_INTERSECTS, val: new Set(blockedCategories) });
      onFieldUpdate(field, { op: Intersect.NOT_INTERSECTS, val: blockedCategories.join(',') });
    }
  }, [blockedCategories, field, onRemoveAll, onFieldUpdate, shouldTriggerUpdate, updateParams]);

  return { handleResetToAccountDefault, inheritanceInfoComponent, shouldDisplayResetButton };
};
